import { useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useApp } from "contexts/app-context";
import { transactionStatusToGeneralStatusMap } from "features/businesses/business-details/content/transactions/BusinessTransactionsTable";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import {
  capitalizeFirstLetter,
  formatDateToDDMMYYTTSS,
  handleAmountFormat,
  handleTruncateAmount,
  replaceIdParamsInRouteWithValue,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination from "ui/pagination";
import { Status } from "ui/status";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds, TTableRowData } from "ui/table/types";
import { Heading4, Paragraph4 } from "ui/typography";

import { TransactionsSubRouteName } from "../content";
import { TableHeaderButtons } from "./TableHeaderButtons";
import {
  BUSINESS_BILL_PAYMENTS_TRANSACTIONS_COLUMN,
  DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS,
} from "./const";
import { IBillPaymentsTransactionsTableProps } from "./types";

export const BillPaymentsTransactionsTable = (
  props: IBillPaymentsTransactionsTableProps
) => {
  const {
    billPaymentsTransactionsResult,
    isLoading,
    handleGetBillPaymentsTransactions,
    billsPaymentsTransactionAnalytics,
    isLoadingBillPaymentsTransactionAnalytics,
  } = props;
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [
    selectedBillPaymentsTransactionIds,
    setSelectedBillPaymentsTransactionIds,
  ] = useState<TSelectedTableRowIds>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data: transfersTransactions, ...paginationData } =
    billPaymentsTransactionsResult;
  const { handleSaveTransactionRefId } = useApp();

  const handleViewTransactionDetails = (transactionRowData: TTableRowData) => {
    const transactionId = transactionRowData["id"] as string;
    const transactionReference = transactionRowData["reference"] as string;
    handleSaveTransactionRefId({
      reference: transactionReference,
      id: transactionId,
    });
    const transactionDetailsRoute = replaceIdParamsInRouteWithValue({
      route: ROUTES.TRANSACTION_DETAILS_PAGE.replace(
        ":transactionRef",
        transactionReference
      ),
      idParamsName: ":transactionType",
      value: TransactionsSubRouteName.BILLS_PAYMENTS,
    });
    navigate(transactionDetailsRoute);
  };

  const formattedData = transfersTransactions.map((transaction) => {
    const {
      id,
      // walletId,
      createdAt,
      accountName,
      email,
      reference,
      amount,
      description,
      status,
    } = transaction;

    const textStyle = { color: "#667085" };
    const amountInNaira = handleTruncateAmount(amount) / ONE_NAIRA_IN_KOBO;

    return {
      Customer: (
        <Box className="flex items-center gap-3">
          <Box>
            <Heading4
              fontSize="14px"
              fontWeight="600"
              lineHeight="20px"
              textColor="#09090B"
              className="capitalize"
            >
              {accountName || "N/A"}
            </Heading4>
            <Paragraph4
              fontSize="12px"
              lineHeight="16px"
              textColor="#718096"
            >
              {email}
            </Paragraph4>
          </Box>
        </Box>
      ),
      Reference: <Box style={textStyle}>{reference}</Box>,
      Amount: (
        <p className="text-sm text-[#1A202C] font-semibold">
          {handleAmountFormat({
            value: String(amountInNaira),
            includeNairaSymbol: true,
            shouldFormatZero: true,
            isFormattedAmtNotPadded: true,
          })}
        </p>
      ),
      Description: (
        <Box className="flex flex-col justify-center items-start">
          <p className="text-sm text-[#101828]">{description}</p>
          {/* <p className="text-sm text-[#667085]">{descriptionFooter}</p> */}
        </Box>
      ),
      Status: (
        <Status
          status={transactionStatusToGeneralStatusMap[status]}
          text={capitalizeFirstLetter(status)}
          className="w-fit"
        />
      ),
      "Date and Time": (
        <Box style={textStyle}>{formatDateToDDMMYYTTSS(createdAt)}</Box>
      ),
      id,
      reference,
    };
  });

  return (
    <TableWrapper className="!p-0">
      <TableHeaderButtons
        handleGetTransactionsData={handleGetBillPaymentsTransactions}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
        transactionAnalytics={billsPaymentsTransactionAnalytics}
        isLoadingTransactionAnalytics={
          isLoadingBillPaymentsTransactionAnalytics
        }
      />
      <Table
        columns={BUSINESS_BILL_PAYMENTS_TRANSACTIONS_COLUMN}
        data={formattedData}
        isRowClickable
        hasCheckbox
        onRowClick={handleViewTransactionDetails}
        isLoading={isLoading && !isRefreshing}
        selectedTableRowIds={selectedBillPaymentsTransactionIds}
        setSelectedTableRowIds={setSelectedBillPaymentsTransactionIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={transfersTransactions}
        defaultPaginationParams={
          DEFAULT_BUSINESS_TRANSACTIONS_PAGINATION_PARAMS
        }
        tableRef={tableRef}
        isLoadingTable={isLoading}
      />
    </TableWrapper>
  );
};
